import type { Deal } from "@domain/deal";
import type { User } from "@domain/user";
import type { Job } from "@domain/job";
import type { JobFlags } from "@domain/job";
import type { LogData } from "@rogoag/shared-ts";
import type { IJobCreation } from "@infra/interfaces/jobs/IJobCreation";
import type { ILogger } from "@infra/interfaces/logger/ILogger";
import {
	type JobCreationContext,
	associateExternalGrowers,
	addNewSalespersons,
	associateSalespersonsAndBranches,
	prepareJobsForCreation,
} from "./jobCreationOrchestration";

export interface SubmitJobsUseCaseDeps {
	jobCreationApi: IJobCreation;
	getCurrentUser(): Promise<User>;
	getDealsByIds(ids: string[]): Promise<Deal[]>;
	createJobs(jobs: Partial<Job>[]): Promise<Job[]>;
	logger: ILogger;
}

/**
 * Job-submission use cases. `submitJobs` orchestrates the full creation
 * pipeline (associate growers, add salespersons, link branches, prepare
 * payload, create) by composing the helpers in `jobCreationOrchestration`
 * with the injected `IJobCreation` port and a few additional dependencies.
 *
 * Viewmodels and components should use this instead of calling
 * `API.Data.Jobs.create.many` directly — the API layer is transport-only and
 * does not perform orchestration.
 */
export class SubmitJobsUseCase {
	constructor(private readonly deps: SubmitJobsUseCaseDeps) {}

	async submitJobs(jobs: Partial<Job>[], source?: JobFlags): Promise<Job[]> {
		const user = await this.deps.getCurrentUser();
		const uniqueDealIds = Array.from(new Set(jobs.filter((job) => job.dealIds?.[0]).map((job) => job.dealIds![0])));
		const deals = uniqueDealIds.length > 0 ? await this.deps.getDealsByIds(uniqueDealIds) : [];

		const logData: LogData = {
			function: "SubmitJobsUseCase.submitJobs",
			jobs_count: jobs.length,
			source,
			user: user.email,
		};

		const ctx: JobCreationContext = {
			userEmail: user.email,
			userCompanyIds: user.companyIds,
			isAdmin: user.isAdmin,
			source,
			deals,
			logger: this.deps.logger,
		};

		// Growers and salespersons are independent — parallelize.
		// Branch association depends on salesperson IDs, so it runs after.
		await Promise.all([
			associateExternalGrowers(jobs, ctx, this.deps.jobCreationApi, { ...logData }),
			addNewSalespersons(jobs, ctx, this.deps.jobCreationApi, { ...logData }),
		]);
		await associateSalespersonsAndBranches(jobs, this.deps.jobCreationApi, logData, this.deps.logger);
		const jobsToCreate = prepareJobsForCreation(jobs, ctx, logData);

		logData.function = "SubmitJobsUseCase.submitJobs.creatingJobs";
		this.deps.logger.log("Creating jobs", logData);
		return await this.deps.createJobs(jobsToCreate);
	}

	/** Convenience wrapper for the single-job case. */
	async submitJob(job: Partial<Job>, source?: JobFlags): Promise<Job> {
		const [created] = await this.submitJobs([job], source);
		return created;
	}
}
